import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  LayoutDashboard,
  Video,
  Award,
  ShoppingBag,
  User,
  Settings,
  LogOut,
  Calendar,
  Clock,
  ChevronRight,
  Play,
  FolderOpen,
  Download,
  FileText,
  ExternalLink,
  Eye,
  Loader2,
} from 'lucide-react';
import { registrationsAPI, certificatesAPI, ordersAPI, webinarsAPI } from '@/services/api';
import { format } from 'date-fns';
import { formatET, getETAbbr } from '@/utils/dateUtils';
import { toast } from 'sonner';
import InvoicePreview from '@/components/invoice/InvoicePreview';

const sidebarItems = [
  { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
  { name: 'My Webinars', href: '/dashboard/webinars', icon: Video },
  { name: 'Certificates', href: '/dashboard/certificates', icon: Award },
  { name: 'Orders', href: '/dashboard/orders', icon: ShoppingBag },
  { name: 'Profile', href: '/dashboard/profile', icon: User },
];

export default function DashboardLayout() {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-zinc-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-zinc-50 pt-20" data-testid="dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 fixed left-0 top-20 bottom-0 bg-zinc-900 border-r border-zinc-900 overflow-y-auto">
          <div className="p-6">
            {/* User Info */}
            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-zinc-900">
              <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center text-zinc-900 font-bold text-lg">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div>
                <p className="font-medium text-white">{user?.first_name} {user?.last_name}</p>
                <p className="text-sm text-zinc-400">{user?.role}</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="space-y-1">
              {sidebarItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-emerald-500 text-zinc-900'
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                  data-testid={`sidebar-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              ))}
            </nav>

            {/* Logout Button */}
            <div className="mt-8 pt-6 border-t border-zinc-900">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-zinc-400 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 p-6 lg:p-8">
          {/* Mobile Menu */}
          <div className="lg:hidden mb-6 overflow-x-auto">
            <div className="flex gap-2 pb-2">
              {sidebarItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                    isActive(item.href)
                      ? 'bg-zinc-900 text-white'
                      : 'bg-white text-zinc-600 border border-zinc-200'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.name}</span>
                </Link>
              ))}
            </div>
          </div>

          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Dashboard Overview Component
export function DashboardOverview() {
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [regsRes, certsRes] = await Promise.all([
        registrationsAPI.getMy(),
        certificatesAPI.getMy(),
      ]);
      setRegistrations(regsRes.data);
      setCertificates(certsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return formatET(dateStr, 'MMM dd, yyyy');
  };

  // Helper function to check if webinar is currently live
  const isWebinarLive = (scheduledDate, durationMinutes) => {
    if (!scheduledDate) return false;
    const now = new Date();
    const start = new Date(scheduledDate);
    const end = new Date(start.getTime() + (durationMinutes || 60) * 60000);
    return now >= start && now <= end;
  };

  // Helper function to check if webinar is starting soon (within 30 minutes)
  const isStartingSoon = (scheduledDate) => {
    if (!scheduledDate) return false;
    const now = new Date();
    const start = new Date(scheduledDate);
    const diffMinutes = (start - now) / 60000;
    return diffMinutes > 0 && diffMinutes <= 30;
  };

  // Helper function to get time remaining until webinar starts
  const getTimeRemaining = (scheduledDate) => {
    if (!scheduledDate) return null;
    const now = new Date();
    const start = new Date(scheduledDate);
    const diff = start - now;
    
    if (diff <= 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const recentWebinars = [...registrations]
    .sort((a, b) => {
      const aDate = a.registered_at || a.created_at || '';
      const bDate = b.registered_at || b.created_at || '';
      return new Date(bDate) - new Date(aDate);
    })
    .slice(0, 5);

  return (
    <div data-testid="dashboard-overview">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-zinc-950 mb-2">
          Welcome back, {user?.first_name}!
        </h1>
        <p className="text-zinc-600">
          Here's an overview of your learning journey.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center">
                <Video className="w-6 h-6 text-zinc-900" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Enrolled Webinars</p>
                <p className="text-2xl font-bold text-zinc-950">{registrations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Award className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Certificates Earned</p>
                <p className="text-2xl font-bold text-zinc-950">{certificates.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Play className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">CPE Credits</p>
                <p className="text-2xl font-bold text-zinc-950">
                  {certificates.reduce((sum, c) => sum + (c.cpe_credits || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Webinars */}
      <Card className="border-zinc-200 mb-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl text-zinc-950">
            Recently Purchased Webinars
          </CardTitle>
          <Link to="/dashboard/webinars">
            <Button variant="ghost" size="sm" className="text-zinc-900">
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="animate-pulse flex items-center gap-4">
                  <div className="w-16 h-16 bg-zinc-200 rounded" />
                  <div className="flex-1">
                    <div className="h-4 bg-zinc-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-zinc-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentWebinars.length > 0 ? (
            <div className="space-y-4">
              {recentWebinars.map((reg) => {
                const isLive = isWebinarLive(reg.webinar?.scheduled_date, reg.webinar?.duration_minutes);
                const isSoon = isStartingSoon(reg.webinar?.scheduled_date);
                const timeRemaining = getTimeRemaining(reg.webinar?.scheduled_date);
                
                return (
                  <div
                    key={reg.id}
                    className={`p-4 rounded-lg border transition-all ${
                      isLive ? 'border-2 border-red-500 bg-red-50/30' :
                      isSoon ? 'border-2 border-emerald-500 bg-emerald-50/30' :
                      'border-zinc-200 hover:border-emerald-400'
                    }`}
                  >
                    <div className="flex-1">
                      {/* Status Badges */}
                      <div className="flex items-center gap-2 mb-2">
                        {isLive && (
                          <Badge className="bg-red-600 text-white animate-pulse">
                            <span className="w-2 h-2 bg-white rounded-full mr-2 animate-ping" />
                            LIVE
                          </Badge>
                        )}
                        {!isLive && isSoon && (
                          <Badge className="bg-emerald-500 text-zinc-950">
                            Starting Soon
                          </Badge>
                        )}
                        {reg.variant && (
                          <Badge className="bg-zinc-100 text-zinc-950">
                            {reg.variant}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Title */}
                      <h3 className="font-medium text-zinc-950 mb-2 line-clamp-2">
                        {reg.webinar?.title || reg.title}
                      </h3>
                      
                      {/* Speaker */}
                      {reg.webinar?.speaker_name && (
                        <p className="text-sm text-zinc-600 mb-2">
                          Speaker: {reg.webinar.speaker_name}
                        </p>
                      )}
                      
                      {/* Webinar Code */}
                      {reg.webinar?.webinar_code && (
                        <p className="text-xs font-mono text-zinc-500 mb-2">
                          Code: {reg.webinar.webinar_code}
                        </p>
                      )}
                      
                      {/* Date, Time, Duration */}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(reg.webinar?.scheduled_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {reg.webinar?.scheduled_date && formatET(reg.webinar.scheduled_date, 'h:mm a')} {getETAbbr(reg.webinar?.scheduled_date)}
                        </span>
                        <span>{reg.webinar?.duration_minutes || 60} min</span>
                      </div>
                      
                      {/* Countdown */}
                      {timeRemaining && timeRemaining.totalMs > 0 && !isLive && !isSoon && (
                        <div className="flex items-center gap-2 text-sm text-zinc-900 bg-zinc-50 p-2 rounded inline-flex">
                          <Clock className="w-4 h-4" />
                          <span>
                            {timeRemaining.days > 0 && `${timeRemaining.days}d `}
                            {timeRemaining.hours}h {timeRemaining.minutes}m
                          </span>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="mt-3 flex gap-2">
                        <Link to={`/webinars/${reg.webinar?.slug || reg.webinar_id || reg.item_id}`}>
                          <Button variant="outline" size="sm" className="border-zinc-900 text-zinc-700">
                            View Details
                          </Button>
                        </Link>
                        {reg.webinar?.zoom_join_url && (
                          <a href={reg.webinar.zoom_join_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" className={`${
                              isLive ? 'bg-red-600 hover:bg-red-700' :
                              isSoon ? 'bg-emerald-500 hover:bg-emerald-400 text-zinc-950' :
                              'bg-zinc-900 hover:bg-zinc-800'
                            } text-white`}>
                              <Play className="w-4 h-4 mr-2" />
                              {isLive ? 'Join Live!' : 'Join via Zoom'}
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Video className="w-12 h-12 mx-auto text-zinc-400 mb-3" />
              <p className="text-zinc-600 mb-4">No upcoming webinars</p>
              <Link to="/webinars">
                <Button className="bg-zinc-900 hover:bg-zinc-800 text-white">
                  Browse Webinars
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Certificates */}
      <Card className="border-zinc-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl text-zinc-950">
            Recent Certificates
          </CardTitle>
          <Link to="/dashboard/certificates">
            <Button variant="ghost" size="sm" className="text-zinc-900">
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {certificates.length > 0 ? (
            <div className="space-y-4">
              {certificates.slice(0, 3).map((cert) => (
                <div
                  key={cert.id}
                  className="flex items-center gap-4 p-4 rounded-lg border border-zinc-200"
                >
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Award className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-zinc-950">{cert.webinar_title}</h3>
                    <p className="text-sm text-zinc-500">
                      Issued: {formatDate(cert.issue_date)} • {cert.cpe_credits} CPE Credits
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="border-zinc-900 text-zinc-700">
                    View
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Award className="w-12 h-12 mx-auto text-zinc-400 mb-3" />
              <p className="text-zinc-600">Complete webinars to earn certificates</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// My Webinars Component with Countdown Timers and Live Badges
// Recording Access Button Component
function RecordingAccessButton({ webinarId }) {
  const [loading, setLoading] = useState(false);

  const handleAccess = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('grc_token');
      const parsedToken = token ? JSON.parse(token) : null;
      const response = await fetch(`${API_URL}/api/recordings/access/${webinarId}`, {
        headers: { 'Authorization': `Bearer ${parsedToken}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        toast.error(err.detail || 'Unable to access recording');
        return;
      }
      
      const data = await response.json();
      
      if (data.video_url) {
        window.open(data.video_url, '_blank');
        toast.success('Recording download started');
      } else if (data.audio_url) {
        window.open(data.audio_url, '_blank');
        toast.success('Audio download started');
      } else {
        toast.error('Recording not yet available for this webinar');
      }
    } catch (error) {
      toast.error('Failed to access recording');
    }
    setLoading(false);
  };

  return (
    <Button
      size="sm"
      variant="outline"
      className="border-purple-500 text-purple-600 hover:bg-purple-50"
      onClick={handleAccess}
      disabled={loading}
      data-testid="access-recording-btn"
    >
      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
      Access Recording
    </Button>
  );
}

export function MyWebinars() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Materials state
  const [materialsDialogOpen, setMaterialsDialogOpen] = useState(false);
  const [selectedWebinarMaterials, setSelectedWebinarMaterials] = useState([]);
  const [selectedWebinarTitle, setSelectedWebinarTitle] = useState('');
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  useEffect(() => {
    fetchRegistrations();
    
    // Update current time every second for countdown
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchRegistrations = async () => {
    try {
      const response = await registrationsAPI.getMy();
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewMaterials = async (webinarId, webinarTitle) => {
    setSelectedWebinarTitle(webinarTitle);
    setLoadingMaterials(true);
    setMaterialsDialogOpen(true);
    
    try {
      const response = await webinarsAPI.getMaterials(webinarId);
      setSelectedWebinarMaterials(response.data?.materials || []);
    } catch (error) {
      console.error('Error fetching materials:', error);
      toast.error('Unable to load materials');
      setSelectedWebinarMaterials([]);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return format(new Date(dateStr), 'MMM dd, yyyy h:mm a');
  };

  // Calculate time remaining until webinar
  const getTimeRemaining = (scheduledDate) => {
    if (!scheduledDate) return null;
    
    const webinarTime = new Date(scheduledDate);
    const diff = webinarTime.getTime() - currentTime.getTime();
    
    if (diff <= 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds, totalMs: diff };
  };

  // Check if webinar is live (within the scheduled time + duration)
  const isWebinarLive = (scheduledDate, durationMinutes) => {
    if (!scheduledDate) return false;
    
    const webinarStart = new Date(scheduledDate);
    const webinarEnd = new Date(webinarStart.getTime() + (durationMinutes || 90) * 60 * 1000);
    
    return currentTime >= webinarStart && currentTime <= webinarEnd;
  };

  // Check if webinar is starting soon (within 15 minutes)
  const isStartingSoon = (scheduledDate) => {
    if (!scheduledDate) return false;
    
    const webinarTime = new Date(scheduledDate);
    const diff = webinarTime.getTime() - currentTime.getTime();
    
    return diff > 0 && diff <= 15 * 60 * 1000;
  };

  // Sort registrations: Live first, then Starting Soon, then by most recent purchase
  const sortedRegistrations = [...registrations].sort((a, b) => {
    const aIsLive = isWebinarLive(a.webinar?.scheduled_date, a.webinar?.duration_minutes);
    const bIsLive = isWebinarLive(b.webinar?.scheduled_date, b.webinar?.duration_minutes);
    const aIsSoon = isStartingSoon(a.webinar?.scheduled_date);
    const bIsSoon = isStartingSoon(b.webinar?.scheduled_date);
    
    if (aIsLive && !bIsLive) return -1;
    if (!aIsLive && bIsLive) return 1;
    if (aIsSoon && !bIsSoon) return -1;
    if (!aIsSoon && bIsSoon) return 1;
    
    // Sort by most recently purchased first
    const aDate = a.registered_at || a.created_at || '';
    const bDate = b.registered_at || b.created_at || '';
    return new Date(bDate) - new Date(aDate);
  });

  return (
    <div data-testid="my-webinars">
      <h1 className="font-heading text-3xl font-bold text-zinc-950 mb-6">
        My Webinars
      </h1>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-24 h-24 bg-zinc-200 rounded" />
                <div className="flex-1">
                  <div className="h-5 bg-zinc-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-zinc-200 rounded w-1/2" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : sortedRegistrations.length > 0 ? (
        <div className="space-y-4">
          {sortedRegistrations.map((reg) => {
            const isLive = isWebinarLive(reg.webinar?.scheduled_date, reg.webinar?.duration_minutes);
            const isSoon = isStartingSoon(reg.webinar?.scheduled_date);
            const timeRemaining = getTimeRemaining(reg.webinar?.scheduled_date);
            const isUpcoming = timeRemaining && timeRemaining.totalMs > 0;
            
            return (
              <Card 
                key={reg.id} 
                className={`border-zinc-200 transition-all ${
                  isLive ? 'border-2 border-red-500 bg-red-50/30' : 
                  isSoon ? 'border-2 border-emerald-500 bg-emerald-50/30' : 
                  'hover:border-emerald-400'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Status Badges */}
                          <div className="flex items-center gap-2 mb-2">
                            {isLive && (
                              <Badge className="bg-red-600 text-white animate-pulse" data-testid="live-badge">
                                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-ping" />
                                LIVE
                              </Badge>
                            )}
                            {!isLive && isSoon && (
                              <Badge className="bg-emerald-500 text-zinc-950" data-testid="starting-soon-badge">
                                Starting Soon
                              </Badge>
                            )}
                            {reg.attended && (
                              <Badge className="bg-green-100 text-green-700">
                                Completed
                              </Badge>
                            )}
                            {/* Variant Badge */}
                            {reg.variant && (
                              <Badge className="bg-zinc-100 text-zinc-950">
                                {reg.variant}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Webinar Title */}
                          <h3 className="font-heading text-xl font-semibold text-zinc-950 mb-3">
                            {reg.webinar?.title || reg.title}
                          </h3>
                          
                          {/* Speaker Info */}
                          {reg.webinar?.speaker_name && (
                            <p className="text-sm text-zinc-600 mb-2">
                              <span className="font-medium">Speaker:</span> {reg.webinar.speaker_name}
                              {reg.webinar?.speaker_credentials && (
                                <span className="text-zinc-500"> ({reg.webinar.speaker_credentials})</span>
                              )}
                            </p>
                          )}
                          
                          {/* Webinar Code */}
                          {reg.webinar?.webinar_code && (
                            <p className="text-xs font-mono text-zinc-500 mb-2">
                              Code: {reg.webinar.webinar_code}
                            </p>
                          )}
                          
                          {/* Date, Time, Timezone, Duration */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-600 mb-3">
                            <span className="flex items-center gap-1.5">
                              <Calendar className="w-4 h-4 text-zinc-700" />
                              {formatDate(reg.webinar?.scheduled_date)}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Clock className="w-4 h-4 text-zinc-700" />
                              {reg.webinar?.scheduled_date && formatET(reg.webinar.scheduled_date, 'h:mm a')} {getETAbbr(reg.webinar?.scheduled_date)}
                            </span>
                            <span className="text-zinc-500">
                              Duration: {reg.webinar?.duration_minutes || 60} minutes
                            </span>
                          </div>
                          
                          {/* Countdown Timer for Upcoming Webinars */}
                          {isUpcoming && !isLive && !isSoon && (
                            <div className="mt-3 p-3 bg-zinc-50 rounded-lg inline-flex items-center gap-4" data-testid="countdown-timer">
                              <Clock className="w-5 h-5 text-zinc-900" />
                              <div className="flex items-center gap-3">
                                {timeRemaining.days > 0 && (
                                  <div className="text-center">
                                    <span className="text-xl font-bold text-zinc-950">{timeRemaining.days}</span>
                                    <span className="text-xs text-zinc-500 ml-1">days</span>
                                  </div>
                                )}
                                <div className="text-center">
                                  <span className="text-xl font-bold text-zinc-950">{timeRemaining.hours}</span>
                                  <span className="text-xs text-zinc-500 ml-1">hrs</span>
                                </div>
                                <div className="text-center">
                                  <span className="text-xl font-bold text-zinc-950">{timeRemaining.minutes}</span>
                                  <span className="text-xs text-zinc-500 ml-1">min</span>
                                </div>
                                {timeRemaining.days === 0 && (
                                  <div className="text-center">
                                    <span className="text-xl font-bold text-zinc-950">{timeRemaining.seconds}</span>
                                    <span className="text-xs text-zinc-500 ml-1">sec</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="mt-4 flex flex-wrap gap-3">
                        <Link to={`/webinars/${reg.webinar?.slug || reg.webinar_id || reg.item_id}`}>
                          <Button variant="outline" size="sm" className="border-zinc-900 text-zinc-700">
                            View Details
                          </Button>
                        </Link>
                        
                        {/* Materials Button - Show if webinar has materials */}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleViewMaterials(
                            reg.webinar_id || reg.item_id || reg.webinar?.id, 
                            reg.webinar?.title || reg.title
                          )}
                          data-testid="view-materials-btn"
                        >
                          <FolderOpen className="w-4 h-4 mr-2" />
                          Materials
                        </Button>
                        
                        {/* Join Zoom Button - Always show if zoom link available */}
                        {reg.webinar?.zoom_join_url && (
                          <a href={reg.webinar.zoom_join_url} target="_blank" rel="noopener noreferrer">
                            <Button 
                              size="sm" 
                              className={`${
                                isLive 
                                  ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                                  : isSoon
                                  ? 'bg-emerald-500 hover:bg-emerald-400 text-zinc-950'
                                  : 'bg-zinc-900 hover:bg-zinc-800 text-white'
                              } font-bold`}
                              data-testid="join-webinar-btn"
                            >
                              <Play className="w-4 h-4 mr-2" />
                              {isLive ? 'Join Live Now!' : isSoon ? 'Join Webinar' : 'Join via Zoom'}
                            </Button>
                          </a>
                        )}
                        
                        {/* Placeholder if no zoom link yet */}
                        {!reg.webinar?.zoom_join_url && !reg.attended && (
                          <Button size="sm" variant="outline" className="border-zinc-300 text-zinc-500" disabled>
                            Zoom Link Coming Soon
                          </Button>
                        )}
                        
                        {/* Recording Access Button - Show for download/recording variants */}
                        {(reg.variant || '').toLowerCase().match(/download|recording|combo|on-demand/) && (
                          <RecordingAccessButton webinarId={reg.webinar_id || reg.item_id || reg.webinar?.id} />
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-zinc-200">
          <CardContent className="p-12 text-center">
            <Video className="w-16 h-16 mx-auto text-zinc-400 mb-4" />
            <h3 className="font-heading text-xl font-semibold text-zinc-950 mb-2">
              No Webinars Yet
            </h3>
            <p className="text-zinc-600 mb-6">
              Browse our catalog and register for your first webinar.
            </p>
            <Link to="/webinars">
              <Button className="bg-zinc-900 hover:bg-zinc-800 text-white">
                Browse Webinars
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Materials Dialog */}
      <Dialog open={materialsDialogOpen} onOpenChange={setMaterialsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-zinc-900" />
              Webinar Materials
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-sm text-zinc-500 mb-4 font-medium truncate" title={selectedWebinarTitle}>
            {selectedWebinarTitle}
          </p>
          
          {loadingMaterials ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-zinc-900 border-t-transparent rounded-full" />
            </div>
          ) : selectedWebinarMaterials.length > 0 ? (
            <div className="space-y-3">
              {selectedWebinarMaterials.map((material, index) => (
                <div 
                  key={material.id || index}
                  className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-zinc-900" />
                    </div>
                    <div>
                      <p className="font-medium text-zinc-700 text-sm">{material.name}</p>
                      <p className="text-xs text-zinc-500">
                        {material.file_type?.toUpperCase()} • {formatFileSize(material.file_size)}
                      </p>
                    </div>
                  </div>
                  <a 
                    href={material.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    download
                  >
                    <Button size="sm" variant="outline" className="gap-2 border-zinc-900 text-zinc-700">
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FolderOpen className="w-12 h-12 mx-auto text-zinc-400 mb-3" />
              <p className="text-zinc-500">No materials available for this webinar yet.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Certificates Component
export function MyCertificates() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await certificatesAPI.getMy();
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return format(new Date(dateStr), 'MMMM dd, yyyy');
  };

  return (
    <div data-testid="my-certificates">
      <h1 className="font-heading text-3xl font-bold text-zinc-950 mb-6">
        My Certificates
      </h1>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-8">
                <div className="h-6 bg-zinc-200 rounded w-3/4 mb-4" />
                <div className="h-4 bg-zinc-200 rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : certificates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {certificates.map((cert) => (
            <Card key={cert.id} className="border-zinc-200 overflow-hidden">
              <div className="bg-zinc-900 p-6 text-center">
                <Award className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
                <p className="text-emerald-400 uppercase tracking-wide text-sm font-medium">
                  Certificate of Completion
                </p>
              </div>
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-zinc-950 mb-2">
                  {cert.webinar_title}
                </h3>
                <p className="text-zinc-600 text-sm mb-4">
                  Awarded to: {cert.user_name}
                </p>
                <div className="flex items-center justify-between text-sm text-zinc-500 mb-4">
                  <span>Issued: {formatDate(cert.issue_date)}</span>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {cert.cpe_credits} CPE Credits
                  </Badge>
                </div>
                <p className="text-xs text-zinc-400 mb-4">
                  Certificate #: {cert.certificate_number}
                </p>
                <Button className="w-full bg-zinc-900 hover:bg-zinc-800 text-white">
                  Download Certificate
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-zinc-200">
          <CardContent className="p-12 text-center">
            <Award className="w-16 h-16 mx-auto text-zinc-400 mb-4" />
            <h3 className="font-heading text-xl font-semibold text-zinc-950 mb-2">
              No Certificates Yet
            </h3>
            <p className="text-zinc-600 mb-6">
              Complete webinars to earn your certificates.
            </p>
            <Link to="/webinars">
              <Button className="bg-zinc-900 hover:bg-zinc-800 text-white">
                Browse Webinars
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Orders Component
export function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await ordersAPI.getMy();
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date with time and EST/EDT timezone
  const formatDateTimeEST = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/New_York',
      timeZoneName: 'short'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: 'bg-green-100 text-green-700 border-green-200',
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      payment_pending: 'bg-orange-100 text-orange-700 border-orange-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200',
      refunded: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    };
    return statusConfig[status] || 'bg-zinc-100 text-zinc-700 border-zinc-200';
  };

  const getItemTitle = (item) => {
    return item.title || item.webinar_title || 'Webinar';
  };

  const getItemVariant = (item) => {
    return item.variant || item.access_type || '-';
  };

  // Get the correct price for an order - check total, total_amount, subtotal
  const getOrderPrice = (order) => {
    const t = parseFloat(order.total) || 0;
    const ta = parseFloat(order.total_amount) || 0;
    const st = parseFloat(order.subtotal) || 0;
    // Return the first non-zero value in priority order
    if (t > 0) return t;
    if (ta > 0) return ta;
    if (st > 0) return st;
    // Fallback: sum up item prices
    if (order.items && order.items.length > 0) {
      return order.items.reduce((sum, item) => sum + getItemPrice(item), 0);
    }
    return 0;
  };

  // Get item price - check price, total_price, total, unit_price
  const getItemPrice = (item) => {
    const p = parseFloat(item.price) || 0;
    const tp = parseFloat(item.total_price) || 0;
    const t = parseFloat(item.total) || 0;
    const up = parseFloat(item.unit_price) || 0;
    if (p > 0) return p;
    if (tp > 0) return tp;
    if (t > 0) return t;
    if (up > 0) return up * (item.quantity || 1);
    return 0;
  };

  // Download invoice
  // Transform order data for InvoicePreview component
  const transformOrderForPreview = (order) => {
    if (!order) return null;
    const total = order.calculated_total || order.total || order.total_amount || order.subtotal || 0;
    return {
      order_number: order.order_number || order.legacy_order_number,
      created_at: order.created_at,
      status: order.status,
      payment_status: order.status === 'completed' ? 'paid' : 'pending',
      payment_method: order.payment_method || 'Credit Card',
      customer_name: order.customer_name || `${order.billing_first_name || ''} ${order.billing_last_name || ''}`.trim(),
      billing_email: order.billing_email || order.customer_email,
      billing_phone: order.billing_phone,
      billing_company: order.billing_company,
      billing_address: order.billing_address,
      billing_city: order.billing_city,
      billing_state: order.billing_state,
      billing_zip: order.billing_zip,
      billing_country: order.billing_country,
      items: (order.items || []).map(item => ({
        webinar_code: item.webinar_code || item.model,
        webinar_title: item.webinar_title || item.title || item.name,
        variant: item.variant || item.variant_normalized || 'Webinar Access',
        quantity: item.quantity || 1,
        unit_price: item.unit_price || item.price || 0,
        total_price: item.total_price || item.total || item.unit_price || item.price || 0,
        scheduled_date: item.scheduled_date,
        speaker: item.speaker
      })),
      subtotal: order.subtotal || total,
      discount_amount: order.discount_amount || 0,
      tax_amount: order.tax_amount || 0,
      total_amount: total
    };
  };

  const handleDownloadInvoice = async (order) => {
    try {
      toast.info('Generating invoice PDF...');
      const response = await ordersAPI.getOrderDetails(order.id);
      const orderData = transformOrderForPreview(response.data);

      // Create a temporary container for the invoice preview
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = '800px';
      document.body.appendChild(container);

      const ReactDOM = await import('react-dom/client');
      const root = ReactDOM.createRoot(container);

      const InvoiceWrapper = () => (
        <div id="pdf-render-target">
          <InvoicePreview order={orderData} hideActions={true} />
        </div>
      );

      root.render(<InvoiceWrapper />);
      await new Promise(resolve => setTimeout(resolve, 500));

      const invoiceContent = container.querySelector('#invoice-content');
      if (!invoiceContent) {
        throw new Error('Invoice content not found');
      }

      const html2pdf = (await import('html2pdf.js')).default;
      const invoiceNumber = `INV-${order.order_number?.replace('GRC-WEB-', '').replace('GRC-SEM-', '').replace('OC-', '') || 'ORDER'}`;

      const opt = {
        margin: 0,
        filename: `${invoiceNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(invoiceContent).save();

      root.unmount();
      document.body.removeChild(container);
      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Invoice not available');
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  return (
    <div data-testid="my-orders">
      <h1 className="font-heading text-2xl font-bold text-zinc-950 mb-6">
        Order History
      </h1>

      {loading ? (
        <Card className="border-zinc-200">
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-zinc-200 rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : orders.length > 0 ? (
        <Card className="border-zinc-200 rounded-xl overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="font-semibold" data-testid="th-order-id">Order ID</TableHead>
                    <TableHead className="font-semibold" data-testid="th-webinar-title">Webinar Title</TableHead>
                    <TableHead className="font-semibold" data-testid="th-variant">Variant</TableHead>
                    <TableHead className="font-semibold text-right" data-testid="th-price">Price</TableHead>
                    <TableHead className="font-semibold text-center" data-testid="th-status">Status</TableHead>
                    <TableHead className="font-semibold text-right" data-testid="th-actions">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-zinc-50" data-testid={`order-row-${order.id}`}>
                      <TableCell className="font-mono text-xs text-zinc-950" data-testid="order-id-cell">
                        {order.order_number}
                      </TableCell>
                      <TableCell data-testid="order-title-cell">
                        {order.items && order.items.length > 0 ? (
                          <div>
                            <p className="text-sm text-zinc-700 truncate max-w-[220px]" title={getItemTitle(order.items[0])}>
                              {getItemTitle(order.items[0])}
                            </p>
                            {order.items.length > 1 && (
                              <p className="text-xs text-zinc-400">+{order.items.length - 1} more</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-zinc-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-zinc-600" data-testid="order-variant-cell">
                        {order.items && order.items.length > 0 ? getItemVariant(order.items[0]) : '-'}
                      </TableCell>
                      <TableCell className="font-semibold text-zinc-800 text-right" data-testid="order-price-cell">
                        ${getOrderPrice(order).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center" data-testid="order-status-cell">
                        <Badge className={`${getStatusBadge(order.status)} border text-xs capitalize`}>
                          {order.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell data-testid="order-actions-cell">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewDetails(order)}
                            className="h-8 w-8"
                            title="View Details"
                            data-testid={`view-details-btn-${order.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {order.status === 'completed' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadInvoice(order)}
                              className="h-8 w-8 text-green-600 hover:text-green-700"
                              title="Download Invoice"
                              data-testid={`download-invoice-btn-${order.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-zinc-200">
          <CardContent className="p-12 text-center">
            <ShoppingBag className="w-16 h-16 mx-auto text-zinc-400 mb-4" />
            <h3 className="font-heading text-xl font-semibold text-zinc-950 mb-2">
              No Orders Yet
            </h3>
            <p className="text-zinc-600 mb-6">
              Your order history will appear here.
            </p>
            <Link to="/webinars">
              <Button className="bg-zinc-900 hover:bg-zinc-800 text-white">
                Browse Webinars
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Order Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl" data-testid="order-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-zinc-900" />
              Order Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Header Info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-zinc-50 rounded-lg">
                <div>
                  <p className="text-xs text-zinc-500">Order ID</p>
                  <p className="font-mono font-medium text-zinc-950 text-sm" data-testid="detail-order-id">{selectedOrder.order_number}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Date, Time & Timezone</p>
                  <p className="text-sm text-zinc-700" data-testid="detail-date-time">{formatDateTimeEST(selectedOrder.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-500">Status</p>
                  <Badge className={`${getStatusBadge(selectedOrder.status)} border text-xs capitalize`} data-testid="detail-status">
                    {selectedOrder.status?.replace('_', ' ')}
                  </Badge>
                </div>
              </div>

              {/* Order Items Table */}
              <div>
                <h4 className="font-medium text-zinc-950 mb-3">Items</h4>
                <div className="border border-zinc-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50">
                        <TableHead className="text-xs font-semibold">Webinar Title</TableHead>
                        <TableHead className="text-xs font-semibold">Variant</TableHead>
                        <TableHead className="text-xs font-semibold">Speaker</TableHead>
                        <TableHead className="text-xs font-semibold">Webinar Code</TableHead>
                        <TableHead className="text-xs font-semibold text-center">Qty</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrder.items?.map((item, idx) => (
                        <TableRow key={idx} data-testid={`detail-item-row-${idx}`}>
                          <TableCell className="text-sm text-zinc-700 max-w-[200px]">
                            <p className="truncate" title={getItemTitle(item)} data-testid={`detail-item-title-${idx}`}>
                              {getItemTitle(item)}
                            </p>
                          </TableCell>
                          <TableCell className="text-sm text-zinc-600" data-testid={`detail-item-variant-${idx}`}>
                            {getItemVariant(item)}
                          </TableCell>
                          <TableCell className="text-sm text-zinc-600" data-testid={`detail-item-speaker-${idx}`}>
                            {item.speaker_name || '-'}
                          </TableCell>
                          <TableCell className="text-xs font-mono text-zinc-500" data-testid={`detail-item-code-${idx}`}>
                            {item.webinar_code || '-'}
                          </TableCell>
                          <TableCell className="text-sm text-zinc-700 text-center" data-testid={`detail-item-qty-${idx}`}>
                            {item.quantity || 1}
                          </TableCell>
                          <TableCell className="text-sm font-semibold text-zinc-800 text-right" data-testid={`detail-item-price-${idx}`}>
                            ${getItemPrice(item).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Order Total */}
              <div className="flex justify-between items-center p-4 bg-zinc-50 rounded-lg">
                <span className="font-medium text-zinc-950">Total Amount</span>
                <span className="text-xl font-bold text-zinc-950" data-testid="detail-total-price">
                  ${getOrderPrice(selectedOrder).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Profile Component - Uses enhanced UserProfilePage
export { default as MyProfile } from './UserProfilePage';
